import { json, type LoaderFunctionArgs, type MetaFunction } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { useTranslation } from "react-i18next";
import i18n from "~/i18next.server";

type RoadmapStatus = "generated" | "in-progress" | "todo";

type RoadmapItem = {
  title: string;
  description: string;
  status: RoadmapStatus;
  expectedDate?: string;
};

export const handle = {
  i18n: "roadmap"
};

export async function loader({ request }: LoaderFunctionArgs) {
  const t = await i18n.getFixedT(request, "roadmap");

  const ROADMAP_ITEMS: RoadmapItem[] = [
    {
      title: t('items.polandHistory.title'),
      description: t('items.polandHistory.description'),
      status: "generated",
      expectedDate: "Q4 2024",
    },
    {
      title: t('items.christmas.title'),
      description: t('items.christmas.description'),
      status: "generated",
      expectedDate: "Q4 2024",
    },
    {
      title: t('items.yearlyEvents.title'),
      description: t('items.yearlyEvents.description'),
      status: "in-progress",
      expectedDate: "Q1 2025",
    },
    {
      title: t('items.USPresidents.title'),
      description: t('items.USPresidents.description'),
      status: "generated",
      expectedDate: "Q1 2025",
    },
    {
      title: t('items.worldWar2.title'),
      description: t('items.worldWar2.description'),
      status: "generated",
      expectedDate: "Q1 2025",
    },
    {
      title: t('items.discoveries.title'),
      description: t('items.discoveries.description'),
      status: "in-progress",
      expectedDate: "Q1 2025",
    },
    {
      title: t('items.missisipiRiver.title'),
      description: t('items.missisipiRiver.description'),
      status: "generated",
      expectedDate: "Q1 2025",
    },
    {
      title: t('items.civilWar.title'),
      description: t('items.civilWar.description'),
      status: "generated",
      expectedDate: "Q1 2025",
    },
    {
      title: t('items.ancientRome.title'),
      description: t('items.ancientRome.description'),
      status: "generated",
      expectedDate: "Q1 2025",
    },
    {
      title: t('items.ancientGreece.title'),
      description: t('items.ancientGreece.description'),
      status: "in-progress",
      expectedDate: "Q1 2025",
    },
    {
      title: t('items.ancientEgypt.title'),
      description: t('items.ancientEgypt.description'),
      status: "in-progress",
      expectedDate: "Q1 2025",
    }
  ];

  return json({
    items: ROADMAP_ITEMS.reverse(),
    meta: {
      title: t('meta.roadmap.title'),
      description: t('meta.roadmap.description'),
      keywords: t('meta.roadmap.keywords')
    }
  });
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) return [];
  
  return [
    { title: data.meta.title },
    { name: "description", content: data.meta.description },
    { name: "keywords", content: data.meta.keywords }
  ];
};

export default function Roadmap() {
  const { t } = useTranslation('roadmap');
  const { items } = useLoaderData<typeof loader>();

  const getStatusColor = (status: RoadmapStatus) => {
    switch (status) {
      case "generated":
        return "bg-green-100 text-green-800";
      case "in-progress":
        return "bg-yellow-100 text-yellow-800";
      case "todo":
        return "bg-gray-100 text-gray-800";
    }
  };

  const StatusBadge = ({ status }: { status: RoadmapStatus }) => (
    <span
      className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${getStatusColor(
        status
      )}`}
    >
      {status === "in-progress" && (
        <svg
          className="animate-spin h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <title>In Progress</title>
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {t(`status.${status}`)}
    </span>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8 text-gray-100">{t('title')}</h1>
      
      <div className="prose max-w-none mb-8 text-gray-200">
        <p>{t('description')}</p>
      </div>

      <div className="space-y-6">
        {items.map((item) => (
          <div
            key={item.title}
            className="border rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">{item.title}</h2>
              <StatusBadge status={item.status} />
            </div>
            <p className="text-gray-600 mb-2">{item.description}</p>
            {item.expectedDate && (
              <p className="text-sm text-gray-400">
                {t('expectedDate')}: {item.expectedDate}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
